import React, { lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Lightbulb, Bot, FlaskConical, Printer, Calendar, ClipboardList, Award, ShieldCheck, Clock, Zap, AlertTriangle, Download } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { FAQ } from '../components/FAQ';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-25">
    {children}
  </motion.div>;
export function LandingPage() {
  return <div className="min-h-screen bg-slate-50 overflow-hidden" data-id="element-26">
      {/* 1. HERO — propuesta directa + CTA arriba */}
      <section className="relative pt-2 pb-12 lg:pt-3 lg:pb-16 overflow-hidden bg-brand-950 text-white" data-id="element-27">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=2038&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-28"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-950/50 to-brand-950" data-id="element-29"></div>

        <div className="container mx-auto px-4 relative z-10 max-w-5xl text-center" data-id="element-30">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-31">
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 text-yellow-300 px-4 py-1.5 rounded-full font-bold text-sm mb-4 border border-yellow-400/30" data-id="element-32">
              <AlertTriangle className="w-4 h-4" data-id="element-33" /> Las Vacaciones de Invierno
              ya comenzaron
            </div>

            <h1 className="md:text-7xl mb-4 text-balance font-[900] text-[40px] leading-tight" data-id="element-34">
              14 DÍAS DE VACACIONES{' '}
              <span className="text-accent-500" data-id="element-35">RESUELTOS</span>
            </h1>

            <p className="text-xl md:text-2xl text-blue-100 mb-6 max-w-3xl mx-auto leading-relaxed" data-id="element-36">
              Actividades listas para mantener a tus hijos entretenidos, creando
              y aprendiendo{' '}
              <strong className="text-white border-b-2 border-accent-500" data-id="element-37">
                sin depender de pantallas.
              </strong>
            </p>

            <div className="flex flex-wrap justify-center md:gap-4 mb-8 text-base md:text-lg font-medium text-blue-50 gap-2" data-id="element-38">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-39">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-40" /> Más de 1.600
                actividades
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-41">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-42" /> Materiales
                fáciles
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-43">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-44" /> Acceso
                inmediato
              </div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="mb-8" data-id="element-45">
              <div className="relative mx-auto max-w-3xl" data-id="element-46">
                <div className="absolute -inset-3 bg-accent-500/20 blur-2xl rounded-[2rem]" data-id="element-47"></div>
                <img src="https://cdn.magicpatterns.com/uploads/jZWK5EYmVVDQFeMtJrMQC9/ChatGPT_Image_17_jun_2026,_06_38_12_p.m..png" alt="Familia armando los 3 kits del Kit Anti-Aburrimiento: robótica, experimentos científicos y papelería 3D" className="relative w-full rounded-2xl md:rounded-3xl shadow-2xl ring-1 ring-white/10" loading="eager" data-id="element-48" />
              </div>
            </motion.div>

            <CTAButton text="👉 QUIERO RESOLVER LAS VACACIONES" data-id="element-49" />

            <p className="mt-4 text-sm text-blue-200 max-w-2xl mx-auto" data-id="element-50">
              Acceso inmediato · Pago único · Garantía total
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. EL PROBLEMA — Antes / Después (una sola sección de dolor) */}
      <section className="py-12 md:py-16 bg-white" data-id="element-51">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-52">
          <FadeIn data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-8 text-center" data-id="element-54">
              El aburrimiento no se soluciona con más pantallas.
            </h2>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-55">
              {/* ANTES */}
              <div className="bg-red-50 border border-red-100 rounded-3xl p-6 md:p-8" data-id="element-56">
                <div className="inline-block bg-red-100 text-red-800 font-black px-4 py-1 rounded-full mb-6 text-sm tracking-widest" data-id="element-57">
                  ANTES
                </div>
                <ul className="space-y-4" data-id="element-58">
                  <li className="flex items-center gap-3 text-red-900 font-medium text-lg" data-id="element-59">
                    <span className="text-2xl" data-id="element-60">😫</span> "Estoy aburrido"
                  </li>
                  <li className="flex items-center gap-3 text-red-900 font-medium text-lg" data-id="element-61">
                    <span className="text-2xl" data-id="element-62">📱</span> Pelea por el celular y
                    la tele
                  </li>
                  <li className="flex items-center gap-3 text-red-900 font-medium text-lg" data-id="element-63">
                    <span className="text-2xl" data-id="element-64">🛋️</span> Horas sin saber qué
                    hacer
                  </li>
                  <li className="flex items-center gap-3 text-red-900 font-medium text-lg" data-id="element-65">
                    <span className="text-2xl" data-id="element-66">💸</span> Salir cuesta caro y el
                    clima no ayuda
                  </li>
                </ul>
              </div>

              {/* DESPUÉS */}
              <div className="bg-green-50 border border-green-200 rounded-3xl p-6 md:p-8 relative overflow-hidden" data-id="element-67">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full blur-3xl opacity-50" data-id="element-68"></div>
                <div className="inline-block bg-green-500 text-white font-black px-4 py-1 rounded-full mb-6 text-sm tracking-widest shadow-sm" data-id="element-69">
                  DESPUÉS CON EL KIT
                </div>
                <ul className="space-y-4 relative z-10" data-id="element-70">
                  <li className="flex items-center gap-3 text-green-900 font-bold text-lg" data-id="element-71">
                    <span className="text-2xl" data-id="element-72">🤖</span> Construyen
                  </li>
                  <li className="flex items-center gap-3 text-green-900 font-bold text-lg" data-id="element-73">
                    <span className="text-2xl" data-id="element-74">🧪</span> Experimentan
                  </li>
                  <li className="flex items-center gap-3 text-green-900 font-bold text-lg" data-id="element-75">
                    <span className="text-2xl" data-id="element-76">🎨</span> Crean
                  </li>
                  <li className="flex items-center gap-3 text-green-900 font-bold text-lg" data-id="element-77">
                    <span className="text-2xl" data-id="element-78">🏆</span> Se sienten orgullosos
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-center text-xl text-slate-700 mt-8 max-w-2xl mx-auto font-medium" data-id="element-79">
              La pregunta que más duele:{' '}
              <strong className="text-slate-900" data-id="element-80">
                ¿los viste crear algo con sus propias manos?
              </strong>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. EL MECANISMO — Efecto Manos-a-la-Obra (el gancho diferencial) */}
      <section className="py-12 md:py-16 bg-brand-900 text-white relative overflow-hidden" data-id="element-81">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-82">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-brand-500 blur-[120px]" data-id="element-83"></div>
          <div className="absolute top-[60%] -right-[10%] w-[50%] h-[50%] rounded-full bg-accent-500 blur-[120px]" data-id="element-84"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-85">
          <FadeIn data-id="element-86">
            <div className="flex justify-center mb-4" data-id="element-87">
              <Lightbulb className="w-12 h-12 text-yellow-400" data-id="element-88" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-center mb-5 leading-tight" data-id="element-89">
              La verdad que nadie te dice sobre el aburrimiento
            </h2>
            <p className="text-xl md:text-2xl text-brand-100 text-center mb-6" data-id="element-90">
              Tus hijos no están aburridos de actividades. Están aburridos
              porque nadie les abrió la puerta correcta.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 md:p-8" data-id="element-91">
              <p className="text-2xl md:text-3xl font-bold text-yellow-400 mb-4 text-center" data-id="element-92">
                Crear con las manos libera la misma dopamina que buscan en el
                teléfono.
              </p>
              <div className="bg-accent-500 text-white font-black text-2xl md:text-3xl py-4 px-6 rounded-2xl text-center transform -rotate-1 shadow-xl" data-id="element-93">
                A eso le llamamos el Efecto Manos-a-la-Obra.
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. QUÉ INCLUYE — una sola vez, visual y escaneable */}
      <section className="py-12 md:py-16 bg-white" id="producto" data-id="element-94">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-95">
          <FadeIn data-id="element-96">
            <div className="text-center mb-10" data-id="element-97">
              <span className="inline-block py-1 px-3 rounded-full bg-brand-100 text-brand-700 font-bold tracking-wider text-sm mb-3 uppercase" data-id="element-98">
                Te presentamos
              </span>
              <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-3" data-id="element-99">
                EL KIT ANTI-ABURRIMIENTO
              </h2>
            </div>

            <div className="relative mx-auto max-w-2xl mb-10" data-id="element-100">
              <div className="absolute -inset-4 bg-brand-500/10 blur-3xl rounded-[2.5rem]" data-id="element-101"></div>
              <img src="https://cdn.magicpatterns.com/uploads/pZeYEn995SZXJDF2TcMYfX/ChatGPT_Image_17_jun_2026,_07_09_37_p.m..png" alt="Caja del Kit Anti-Aburrimiento con los 3 materiales" className="relative w-full rounded-3xl shadow-2xl" loading="lazy" data-id="element-102" />
            </div>

            {/* Los 3 materiales en tarjetas escaneables */}
            <div className="grid md:grid-cols-3 gap-4 mb-6" data-id="element-103">
              <div className="bg-slate-50 rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-104">
                <div className="bg-brand-600 p-5 text-white flex items-center gap-3" data-id="element-105">
                  <Bot className="w-7 h-7 flex-shrink-0" data-id="element-106" />
                  <h4 className="text-lg font-black leading-tight" data-id="element-107">
                    20 Proyectos de Robótica
                  </h4>
                </div>
                <p className="p-5 text-slate-600" data-id="element-108">
                  Arman robots con materiales reciclados y aprenden lógica y
                  física jugando.
                </p>
              </div>
              <div className="bg-slate-50 rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-109">
                <div className="bg-emerald-600 p-5 text-white flex items-center gap-3" data-id="element-110">
                  <FlaskConical className="w-7 h-7 flex-shrink-0" data-id="element-111" />
                  <h4 className="text-lg font-black leading-tight" data-id="element-112">
                    +100 Experimentos Científicos
                  </h4>
                </div>
                <p className="p-5 text-slate-600" data-id="element-113">
                  Volcanes, slime, tinta invisible y más. Un laboratorio en
                  casa.
                </p>
              </div>
              <div className="bg-slate-50 rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-114">
                <div className="bg-purple-600 p-5 text-white flex items-center gap-3" data-id="element-115">
                  <Printer className="w-7 h-7 flex-shrink-0" data-id="element-116" />
                  <h4 className="text-lg font-black leading-tight" data-id="element-117">
                    +1.500 Modelos 3D
                  </h4>
                </div>
                <p className="p-5 text-slate-600" data-id="element-118">
                  Figuras de fútbol, personajes pop y juguetes para armar e
                  imprimir.
                </p>
              </div>
            </div>

            {/* Bonus en una fila compacta */}
            <div className="border-2 border-yellow-200 bg-yellow-50/50 rounded-3xl p-6 mb-8" data-id="element-119">
              <h3 className="text-xl font-black text-center mb-5 flex items-center justify-center gap-2" data-id="element-120">
                <span className="text-2xl" data-id="element-121">🎁</span> + 3 BONUS DE REGALO
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-122">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-yellow-100 flex items-center gap-3" data-id="element-123">
                  <Calendar className="w-7 h-7 text-yellow-500 flex-shrink-0" data-id="element-124" />
                  <span className="font-bold text-sm text-slate-800" data-id="element-125">
                    Planificador de 2 Semanas
                  </span>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-yellow-100 flex items-center gap-3" data-id="element-126">
                  <ClipboardList className="w-7 h-7 text-yellow-500 flex-shrink-0" data-id="element-127" />
                  <span className="font-bold text-sm text-slate-800" data-id="element-128">
                    Lista de Materiales Low-Cost
                  </span>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-yellow-100 flex items-center gap-3" data-id="element-129">
                  <Award className="w-7 h-7 text-yellow-500 flex-shrink-0" data-id="element-130" />
                  <span className="font-bold text-sm text-slate-800" data-id="element-131">
                    Diploma del Pequeño Inventor
                  </span>
                </div>
              </div>
            </div>

            <p className="text-center text-lg font-medium text-slate-600 bg-brand-50 py-3 px-6 rounded-full inline-block mx-auto block w-fit" data-id="element-132">
              <Download className="w-5 h-5 inline-block mr-2 text-brand-600" data-id="element-133" />
              Todo listo para descargar, imprimir y comenzar hoy mismo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 5. COMPARACIÓN DE VALOR — menos que una salida (convierte) */}
      <section className="py-12 md:py-16 bg-slate-50 border-y border-slate-200" data-id="element-134">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-135">
          <FadeIn data-id="element-136">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-10 text-slate-900" data-id="element-137">
              MENOS DE LO QUE CUESTA UNA SOLA SALIDA
            </h2>
            <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-10 mb-6 shadow-sm" data-id="element-138">
              <div className="space-y-4" data-id="element-139">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200" data-id="element-140">
                  <span className="text-lg md:text-xl font-medium text-slate-600 flex items-center gap-3" data-id="element-141">
                    <span className="text-2xl" data-id="element-142">🍿</span> Una salida al cine en
                    familia
                  </span>
                  <span className="text-xl font-bold text-slate-400 line-through" data-id="element-143">
                    $35.000+
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-200" data-id="element-144">
                  <span className="text-lg md:text-xl font-medium text-slate-600 flex items-center gap-3" data-id="element-145">
                    <span className="text-2xl" data-id="element-146">🍔</span> Una cena rápida
                  </span>
                  <span className="text-xl font-bold text-slate-400 line-through" data-id="element-147">
                    $25.000+
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 bg-orange-50 -mx-6 px-6 py-4 rounded-2xl border border-orange-200 shadow-sm transform scale-[1.02]" data-id="element-148">
                  <span className="text-xl md:text-2xl font-black text-slate-900 flex items-center gap-3" data-id="element-149">
                    <span className="text-3xl" data-id="element-150">🤖</span> Kit Anti-Aburrimiento
                  </span>
                  <span className="text-3xl md:text-4xl font-black text-accent-500" data-id="element-151">
                    $12.990
                  </span>
                </div>
              </div>
            </div>
            <p className="text-center text-lg text-slate-600 max-w-2xl mx-auto font-medium" data-id="element-152">
              La diferencia es que este kit{' '}
              <strong className="text-slate-900" data-id="element-153">queda para siempre</strong> y
              te dura todas las vacaciones.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. OFERTA + GARANTÍA + URGENCIA + CTA FINAL (cierre consolidado) */}
      <section className="py-12 md:py-16 bg-slate-900 text-white border-t border-slate-800" data-id="element-154">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-155">
          <FadeIn data-id="element-156">
            {/* Urgencia */}
            <div className="text-center mb-8" data-id="element-157">
              <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-300 px-5 py-2 rounded-full font-bold border border-red-500/30" data-id="element-158">
                <Clock className="w-5 h-5" data-id="element-159" /> ⚠️ Las Vacaciones de Invierno ya
                comenzaron
              </div>
            </div>

            {/* Oferta stack */}
            <div className="bg-white text-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200 mb-8" data-id="element-160">
              <div className="bg-accent-500 text-white text-center py-4" data-id="element-161">
                <h2 className="text-2xl font-black" data-id="element-162">
                  📦 TODO LO QUE RECIBÍS HOY
                </h2>
              </div>
              <div className="p-6 md:p-8" data-id="element-163">
                <ul className="space-y-3 mb-6" data-id="element-164">
                  {[{
                  item: 'Material #1: 20 Proyectos de Robótica',
                  price: '$24.990'
                }, {
                  item: 'Material #2: +100 Experimentos Científicos + 40 Videos',
                  price: '$29.990'
                }, {
                  item: 'Material #3: Fábrica de Juguetes 3D (+1.500 modelos)',
                  price: '$39.990'
                }, {
                  item: 'Bonus #1: Planificador de 2 Semanas',
                  price: '$9.990'
                }, {
                  item: 'Bonus #2: Lista de Materiales Low-Cost',
                  price: '$7.990'
                }, {
                  item: 'Bonus #3: Diploma del Pequeño Inventor',
                  price: '$4.990'
                }].map(({
                  item,
                  price
                }, i) => <li key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0" data-id="element-165">
                      <span className="font-medium flex items-center gap-2 text-sm md:text-base" data-id="element-166">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-167" />{' '}
                        {item}
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0" data-id="element-168">
                        <span className="text-slate-400 line-through text-xs md:text-sm" data-id="element-169">
                          {price}
                        </span>
                        <span className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full text-[10px] md:text-xs" data-id="element-170">
                          GRATIS
                        </span>
                      </span>
                    </li>)}
                </ul>

                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mb-6" data-id="element-171">
                  <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200" data-id="element-172">
                    <span className="font-bold text-slate-700" data-id="element-173">
                      Valor total real:
                    </span>
                    <span className="text-2xl font-black text-slate-400 line-through" data-id="element-174">
                      $117.940
                    </span>
                  </div>
                  <div className="text-center" data-id="element-175">
                    <p className="inline-flex items-center gap-2 bg-red-100 text-red-700 font-bold text-xs px-3 py-1 rounded-full mb-3 uppercase tracking-wide" data-id="element-176">
                      🔥 Oferta de lanzamiento
                    </p>
                    <p className="text-slate-600 font-medium mb-1" data-id="element-177">
                      Hoy, todo el kit + los 3 bonus por:
                    </p>
                    <div className="flex items-end justify-center gap-2" data-id="element-178">
                      <span className="text-5xl md:text-7xl font-black text-accent-500 leading-none" data-id="element-179">
                        $12.990
                      </span>
                    </div>
                    <p className="text-sm text-green-600 font-bold mt-2" data-id="element-180">
                      Ahorrás $104.950 (89% de descuento)
                    </p>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-181">
                      Pago único · Sin suscripciones · Acceso de por vida
                    </p>
                  </div>
                </div>

                <div className="text-center mb-6" data-id="element-182">
                  <CTAButton text="👉 SÍ, QUIERO EL KIT ANTI-ABURRIMIENTO" className="w-full md:w-auto" data-id="element-183" />

                  {/* Pago por transferencia con 15% OFF vía WhatsApp */}
                  <div className="mt-5 pt-5 border-t border-dashed border-slate-200" data-id="element-184">
                    <a href={`https://wa.me/5493516347235?text=${encodeURIComponent('Hola! Quiero abonar con transferencia bancaria el Kit Anti-Aburrimiento y aprovechar el 15% de descuento 🙌')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 w-full md:w-auto bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-base md:text-lg py-3 px-6 rounded-full shadow-[0_8px_30px_rgba(37,211,102,0.4)] transition-colors" data-id="element-185">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current flex-shrink-0" aria-hidden="true" data-id="element-186">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-187" />
                      </svg>
                      💳 Pagar por transferencia (15% OFF)
                    </a>
                    <p className="mt-2 text-xs text-slate-500 font-medium" data-id="element-188">
                      Te coordinamos el pago por WhatsApp y aprovechás un 15% de
                      descuento.
                    </p>
                  </div>
                </div>

                {/* Garantía compacta dentro del cierre */}
                <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-4" data-id="element-189">
                  <ShieldCheck className="w-12 h-12 text-green-600 flex-shrink-0" data-id="element-190" />
                  <p className="text-green-900 font-medium text-sm md:text-base" data-id="element-191">
                    <strong data-id="element-192">Garantía total sin riesgo.</strong> Si no cumple lo
                    prometido, te devolvemos tu dinero.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. FAQ — objeciones de último momento */}
      <section className="py-12 md:py-16 bg-white border-t border-slate-200" data-id="element-193">
        <div className="container mx-auto px-4" data-id="element-194">
          <FadeIn data-id="element-195">
            <h2 className="text-2xl md:text-3xl font-black text-center mb-8 text-slate-900" data-id="element-196">
              Preguntas Frecuentes
            </h2>
            <FAQ data-id="element-197" />
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6 text-center text-xs" data-id="element-198">
        <p data-id="element-199">© 2026 Kit Anti-Aburrimiento. Todos los derechos reservados.</p>
      </footer>
    </div>;
}